/**
 * Browser-only onboarding flags (localStorage).
 * Bump ONBOARDING_STORAGE_VERSION when steps/copy change so users can see a fresh tour.
 */
export const ONBOARDING_TOURS_ENABLED = false;

export const ONBOARDING_STORAGE_KEY = 'galldris_onboarding_v1';

export const TOUR_KEYS = {
  PORTAL_SELECTION: 'portal-selection',
  PROJECT_PORTAL: 'project-portal',
  DASHBOARD: 'dashboard',
  TIMESHEETS: 'timesheets',
  INCIDENTS: 'incidents',
  DELIVERIES: 'deliveries',
  PROJECTS: 'projects',
  CREATE_PROJECT: 'create-project',
  EDIT_PROJECT: 'edit-project',
  ASSETS: 'assets',
  VEHICLES: 'vehicles',
  PROJECT_DASHBOARD: 'project-dashboard',
  PROJECT_DASHBOARD_GOOD_CALLS: 'project-dashboard-good-calls',
  PROJECT_DASHBOARD_GOOD_CALLS_DASHBOARD: 'project-dashboard-good-calls-dashboard',
  PROJECT_DASHBOARD_CLOSE_CALLS: 'project-dashboard-close-calls',
  PROJECT_DASHBOARD_CLOSE_CALLS_DASHBOARD: 'project-dashboard-close-calls-dashboard',
  PROJECT_DASHBOARD_INDUCTIONS: 'project-dashboard-inductions',
  PROJECT_DASHBOARD_INSPECTIONS: 'project-dashboard-inspections',
  PROJECT_DASHBOARD_RAMS: 'project-dashboard-rams',
  PROJECT_DASHBOARD_GIVE_BRIEFING: 'project-dashboard-give-briefing',
  PROJECT_DASHBOARD_VIEW_RAMS: 'project-dashboard-view-rams',
  PROJECT_DASHBOARD_TAKE_REGISTER: 'project-dashboard-take-register',
  INDUCTIONS: 'inductions',
  INSPECTIONS: 'inspections',
  GOOD_CALLS: 'good-calls',
  CLOSE_CALLS: 'close-calls',
  RAMS: 'rams',
  SETTINGS: 'settings',
  MY_DATA: 'my-data',
  SYSTEM_INDUCTION: 'system-induction',
  HEALTH_MEDICAL_EDIT: 'health-medical-edit',
  USER_PORTAL: 'user-portal',
  COMPANY_PORTAL_HOME: 'company-portal-home',
  COMPANY_PORTAL_DASHBOARDS: 'company-portal-dashboards',
  COMPANY_PORTAL_EMPLOYEES: 'company-portal-employees',
  COMPANY_PORTAL_COMPANY_INDUCTION: 'company-portal-company-induction',
  COMPANY_PORTAL_PROJECTS: 'company-portal-projects',
  COMPANY_PORTAL_PROJECT_DETAIL: 'company-portal-project-detail',
  COMPANY_PORTAL_PROJECT_GOOD_CALLS: 'company-portal-project-good-calls',
  COMPANY_PORTAL_PROJECT_GOOD_CALLS_DASHBOARD: 'company-portal-project-good-calls-dashboard',
  COMPANY_PORTAL_PROJECT_CLOSE_CALLS: 'company-portal-project-close-calls',
  COMPANY_PORTAL_PROJECT_CLOSE_CALLS_DASHBOARD: 'company-portal-project-close-calls-dashboard',
  COMPANY_PORTAL_PROJECT_INDUCTIONS: 'company-portal-project-inductions',
  COMPANY_PORTAL_PROJECT_INSPECTIONS: 'company-portal-project-inspections',
  COMPANY_PORTAL_PROJECT_RAMS: 'company-portal-project-rams',
  COMPANY_PORTAL_PROJECT_GIVE_BRIEFING: 'company-portal-project-give-briefing',
  COMPANY_PORTAL_PROJECT_VIEW_RAMS: 'company-portal-project-view-rams',
  COMPANY_PORTAL_PROJECT_TAKE_REGISTER: 'company-portal-project-take-register',
  COMPANY_PORTAL_MAPS: 'company-portal-maps',
  COMPANY_INDUCTION_SEARCH: 'company-induction-search',
  COMPANY_INDUCTION_START: 'company-induction-start',
  HEALTH_MEDICAL_INTRO: 'health-medical-intro',
  HEALTH_MEDICAL_START: 'health-medical-start',
  SITE_INDUCTION_INTRO: 'site-induction-intro',
  SITE_INDUCTION_START: 'site-induction-start'
};

function normalizeState(state) {
  if (!state || typeof state !== 'object') return { completedByKey: {} };
  if (state.completedByKey && typeof state.completedByKey === 'object') {
    return state;
  }
  // Backward compatibility with previous flat format:
  // { portalSelection: true, mainShell: true }
  const completedByKey = {};
  if (state.portalSelection === true) completedByKey[TOUR_KEYS.PORTAL_SELECTION] = true;
  if (state.mainShell === true) completedByKey[TOUR_KEYS.DASHBOARD] = true;
  return { completedByKey };
}

export function readOnboardingState() {
  try {
    const raw = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return { completedByKey: {} };
    const parsed = JSON.parse(raw);
    return normalizeState(parsed);
  } catch {
    return { completedByKey: {} };
  }
}

export function isTourComplete(tourKey) {
  if (!ONBOARDING_TOURS_ENABLED) return true;
  if (!tourKey) return true;
  const state = readOnboardingState();
  return state.completedByKey?.[tourKey] === true;
}

export function setTourComplete(tourKey) {
  if (!ONBOARDING_TOURS_ENABLED || !tourKey) return;
  const state = readOnboardingState();
  const next = {
    ...state,
    completedByKey: {
      ...state.completedByKey,
      [tourKey]: true
    }
  };
  localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(next));
}

export function resetTour(tourKey) {
  if (!ONBOARDING_TOURS_ENABLED || !tourKey) return;
  const state = readOnboardingState();
  const completedByKey = { ...state.completedByKey };
  delete completedByKey[tourKey];
  const next = { ...state, completedByKey };
  localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(next));
}

export function resetAllOnboarding() {
  localStorage.removeItem(ONBOARDING_STORAGE_KEY);
}

/** Remove stray overlays/backdrops that block the first tap on mobile (joyride, closed modals). */
export function clearStuckOverlays() {
  if (typeof document === 'undefined') return;
  document.getElementById('react-joyride-portal')?.remove();
  document
    .querySelectorAll('.react-joyride__overlay, .react-joyride__spotlight, .react-joyride__beacon')
    .forEach((el) => el.remove());
  document.querySelectorAll('.MuiModal-root.MuiModal-hidden .MuiBackdrop-root').forEach((el) => el.remove());
}

/** @deprecated Use clearStuckOverlays */
export const clearJoyrideOverlay = clearStuckOverlays;
